import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

export type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info'

/* Icewall tinted chips: bg color/20, border color/60, text color */
const tones: Record<BadgeTone, string> = {
  neutral: 'border-foreground/30 bg-foreground/10 text-foreground/80',
  brand: 'border-primary/60 bg-primary/20 text-primary',
  success: 'border-success/60 bg-success/20 text-success',
  warning: 'border-warning/60 bg-warning/20 text-warning',
  danger: 'border-danger/60 bg-danger/20 text-danger',
  info: 'border-pending/60 bg-pending/20 text-pending',
}

/* `sm` matches the portal UrgentBadge chip, for pills stacked under a table value. */
const sizes = {
  md: 'rounded-lg text-xs font-medium',
  sm: 'rounded-full text-2xs font-semibold',
}

export default function Badge({
  tone = 'neutral',
  size = 'md',
  className,
  children,
}: {
  tone?: BadgeTone
  size?: keyof typeof sizes
  className?: string
  children: ReactNode
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 border px-2 py-0.5',
        sizes[size],
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

const statusTones: Record<string, BadgeTone> = {
  pending: 'warning',
  pending_payment: 'warning',
  cancelled: 'neutral',
  approved: 'success',
  suspended: 'danger',
  paid: 'brand',
  in_production: 'info',
  in_progress: 'info',
  queued: 'neutral',
  completed: 'success',
  issue: 'danger',
  // Flagged-issue states on the admin issue report.
  open: 'danger',
  resolved: 'success',
  cleared: 'neutral',
  suggested: 'warning',
  overridden: 'neutral',
}

/* Order statuses only. 'paid' alone reads as if nothing happens next, when it
   really means "paid, waiting for the floor to start". Job statuses (queued,
   in_progress) stay raw - staff chips must keep saying exactly where a job is. */
const statusLabels: Record<string, string> = {
  paid: 'paid - awaiting production',
}

export function StateBadge({ state }: { state: string }) {
  return <Badge tone={statusTones[state] ?? 'neutral'}>{statusLabels[state] ?? state.replace(/_/g, ' ')}</Badge>
}
